import { useEffect, useRef, useState } from "react";

const stockSimpleFeatures = [
  {
    iconClass: "ri-flashlight-line",
    color: "#00FF88",
    title: "Registro Flash",
    desc: "Sube productos fácil y rápido. Sin procesos complicados, sin fricción.",
  },
  {
    iconClass: "ri-alarm-warning-line",
    color: "#FF6B35",
    title: "Alertas Inteligentes",
    desc: "El sistema te avisa si queda poco stock. Nunca pierdas ventas por desabastecimiento.",
  },
  {
    iconClass: "ri-bar-chart-2-line",
    color: "#00FF88",
    title: "Reportes Visuales",
    desc: "Conoce tus márgenes y el valor de tu inventario con un clic. Datos claros al instante.",
  },
  {
    iconClass: "ri-global-line",
    color: "#00FF88",
    title: "100% Sincronizado",
    desc: "Entra desde celular, tablet o laptop 24/7. Tu negocio en la palma de tu mano.",
  },
  {
    iconClass: "ri-robot-line",
    color: "#00FF88",
    title: "IA Integrada",
    desc: "Consulta a la inteligencia artificial y obtén un análisis rápido de tu inventario, tendencias de stock y recomendaciones en segundos.",
    badge: "NUEVO",
    badgeColor: "#00FF88",
  },
  {
    iconClass: "ri-file-pdf-line",
    color: "#00FF88",
    title: "Exportación a PDF Profesional",
    desc: "Genera reportes en PDF con diseño profesional listos para presentar a socios, contadores o inversores con un solo clic.",
    badge: "NUEVO",
    badgeColor: "#00FF88",
  },
  {
    iconClass: "ri-customer-service-2-line",
    color: "#00FF88",
    title: "Soporte y Actualizaciones Constantes",
    desc: "Equipo de soporte siempre disponible. Recibes mejoras y nuevas funciones automáticamente sin costo adicional.",
    badge: "NUEVO",
    badgeColor: "#00FF88",
  },
];

const budgettrackFeatures = [
  {
    iconClass: "ri-smartphone-line",
    color: "#FF6B35",
    title: "Movilidad Total",
    desc: "Úsalo en Celular, PC o Tablet. Registra en la nube desde donde estés.",
  },
  {
    iconClass: "ri-time-line",
    color: "#FF6B35",
    title: "Cero Fricción",
    desc: "Adiós al Excel. Registra tus movimientos en 3 clics. Simple y poderoso.",
  },
  {
    iconClass: "ri-pie-chart-2-line",
    color: "#FF6B35",
    title: "Radiografía Visual",
    desc: "Gráficos automáticos para detectar y cortar tus fugas de dinero rápido.",
  },
  {
    iconClass: "ri-robot-line",
    color: "#FF6B35",
    title: "IA Integrada",
    desc: "Pídele a la IA un resumen financiero instantáneo: detecta gastos vampiro, patrones de gasto y oportunidades de ahorro sin esfuerzo.",
    badge: "NUEVO",
    badgeColor: "#FF6B35",
  },
  {
    iconClass: "ri-file-pdf-line",
    color: "#FF6B35",
    title: "Exportación a PDF Profesional",
    desc: "Descarga tus reportes financieros en formato PDF profesional para compartir con tu equipo o tomar decisiones con datos reales.",
    badge: "NUEVO",
    badgeColor: "#FF6B35",
  },
  {
    iconClass: "ri-customer-service-2-line",
    color: "#FF6B35",
    title: "Soporte y Actualizaciones Constantes",
    desc: "Soporte real con personas reales. Además recibes todas las actualizaciones futuras incluidas en tu plan sin pagar nada extra.",
    badge: "NUEVO",
    badgeColor: "#FF6B35",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function SistemasSection() {
  const { ref: problemRef, inView: problemVisible } = useInView();
  const { ref: sisteRef, inView: sisteVisible } = useInView(0.1);

  return (
    <>
      {/* Problem Section */}
      <section
        id="problema"
        ref={problemRef}
        className="bg-[#0a0a0a] py-20 md:py-28 px-6"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        <div
          className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
            problemVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
            Si no mides tus números,{" "}
            <span className="text-[#FF6B35]">tienes un pasatiempo caro.</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#FF6B35] to-transparent mx-auto mb-8 rounded-full" />
          <p className="text-gray-400 text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Si sigues usando <strong className="text-[#00FF88]">Excels complicados</strong>, dejas plata en la
            mesa. Las <strong className="text-[#FF6B35]">fugas de dinero</strong> y los quiebres de stock matan
            tu rentabilidad. Este ecosistema es para que dejes las excusas y operes como un{" "}
            <strong className="text-white">estratega real</strong>.
          </p>
          {/* Pain points */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
            {[
              { icon: "ri-close-circle-line", text: "Ventas perdidas por quiebre de stock", color: "#FF3D00" },
              { icon: "ri-money-dollar-circle-line", text: "Fugas de dinero invisibles cada mes", color: "#FF6B35" },
              { icon: "ri-bar-chart-line", text: "Sin visibilidad de tus márgenes reales", color: "#FF6B35" },
            ].map((item) => (
              <div
                key={item.text}
                className="bg-[#141414] border border-white/5 rounded-xl p-5 flex flex-col items-center gap-3"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${item.icon} text-2xl`} style={{ color: item.color }} />
                </div>
                <p className="text-gray-300 text-sm text-center">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sistemas Section */}
      <section
        id="sistemas"
        ref={sisteRef}
        className="bg-[#080808] py-20 md:py-28 px-6"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        <div className="max-w-7xl mx-auto">
          <div
            className={`text-center mb-16 transition-all duration-700 delay-100 ${
              sisteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-[#00FF88] text-sm font-bold uppercase tracking-widest">Lo que obtienes</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mt-3">
              Dos Sistemas. Un Solo Precio.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Sistema 1: Stock Simple */}
            <div
              className={`group relative bg-[#111111] rounded-2xl overflow-hidden border border-white/5 hover:border-[#00FF88]/30 transition-all duration-500 ${
                sisteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              <div className="h-1.5 w-full bg-gradient-to-r from-[#00FF88] to-[#00cc6a]" />
              <div className="p-8 md:p-10">
                {/* Header */}
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00FF88]/10 rounded-xl text-3xl flex-shrink-0">
                    📦
                  </div>
                  <div>
                    <p className="text-[#00FF88] text-xs font-bold uppercase tracking-widest mb-1">Sistema 1</p>
                    <h3 className="text-white text-2xl md:text-3xl font-black">Stock Simple</h3>
                    <p className="text-gray-500 text-sm mt-1 italic">Tu nuevo centro de mando</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-5">
                  {stockSimpleFeatures.map((f) => (
                    <div key={f.title} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-[#00FF88]/10">
                        <i className={`${f.iconClass} text-lg`} style={{ color: f.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-bold text-base">{f.title}</p>
                          {"badge" in f && f.badge && (
                            <span
                              className="text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
                              style={{ background: `${f.badgeColor}20`, color: f.badgeColor, border: `1px solid ${f.badgeColor}40` }}
                            >
                              {f.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {f.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Promo image */}
                <div className="mt-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=inventory%20management%20software%20dashboard%20interface%20on%20dark%20screen%2C%20product%20list%20with%20stock%20levels%20and%20alerts%2C%20clean%20modern%20UI%20design%20with%20green%20accents%2C%20minimal%20dark%20theme%2C%20professional%20business%20application%20screenshot&width=560&height=280&seq=stock-simple-03&orientation=landscape"
                    alt="Interfaz Stock Simple"
                    className="w-full h-36 object-cover object-top rounded-xl"
                  />
                </div>

                {/* Hook */}
                <p className="mt-6 text-[#00FF88] text-center italic text-base font-semibold">
                  "Imagina tener todo tu negocio en la palma de tu mano... Suena bien, ¿verdad?"
                </p>
              </div>
            </div>

            {/* Sistema 2: Budgettrack */}
            <div
              className={`group relative bg-[#111111] rounded-2xl overflow-hidden border border-white/5 hover:border-[#FF6B35]/30 transition-all duration-500 ${
                sisteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: "350ms" }}
            >
              <div className="h-1.5 w-full bg-gradient-to-r from-[#FF6B35] to-[#FF3D00]" />
              <div className="p-8 md:p-10">
                {/* Header */}
                <div className="flex items-start gap-4 mb-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B35]/10 rounded-xl text-3xl flex-shrink-0">
                    💰
                  </div>
                  <div>
                    <p className="text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-1">Sistema 2</p>
                    <h3 className="text-white text-2xl md:text-3xl font-black">Budgettrack</h3>
                    <p className="text-gray-500 text-sm mt-1 italic">Domina tus números financieros</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-5">
                  {budgettrackFeatures.map((f) => (
                    <div key={f.title} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-[#FF6B35]/10">
                        <i className={`${f.iconClass} text-lg`} style={{ color: f.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-bold text-base">{f.title}</p>
                          {"badge" in f && f.badge && (
                            <span
                              className="text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
                              style={{ background: `${f.badgeColor}20`, color: f.badgeColor, border: `1px solid ${f.badgeColor}40` }}
                            >
                              {f.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {f.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Promo image */}
                <div className="mt-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=financial%20budget%20tracking%20app%20interface%20on%20mobile%20and%20desktop%2C%20expense%20charts%20and%20graphs%20with%20orange%20accents%2C%20clean%20dark%20UI%20design%2C%20income%20expense%20dashboard%2C%20professional%20fintech%20application%20screenshot%2C%20minimal%20modern%20design&width=560&height=280&seq=budgettrack-04&orientation=landscape"
                    alt="Interfaz Budgettrack"
                    className="w-full h-36 object-cover object-top rounded-xl"
                  />
                </div>

                {/* Price highlight */}
                <div className="mt-6 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm">Ambos sistemas juntos por solo</p>
                  <p className="text-white text-4xl font-black mt-1">
                    S/ 25{" "}
                    <span className="text-[#FF6B35] text-lg font-semibold">/año</span>
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Menos de 7 céntimos al día
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
